import type { Preset } from "@/backend/natsstubs/onboarding/models";

import type { OnboardIteration, OnboardTeam, SessionGenerationResult } from "../types";
import { getDefaultSessionName, getQuarterAndYear } from "./dateUtils";

// Types for default onboarding data
interface DefaultTeamData {
  id: string;
  name: string;
}

interface DefaultIterationData {
  id: string;
  name: string;
}

// Defaults for onboarding - not used elsewhere in the app
function getDefaultTeams(): DefaultTeamData[] {
  return [
    { id: crypto.randomUUID(), name: "Team 1" },
    { id: crypto.randomUUID(), name: "Team 2" },
    { id: crypto.randomUUID(), name: "Team 3" },
  ];
}

function getDefaultIterations(): DefaultIterationData[] {
  const { quarter, shortYear } = getQuarterAndYear();
  return [
    { id: crypto.randomUUID(), name: `${shortYear}.${quarter}.1` },
    { id: crypto.randomUUID(), name: `${shortYear}.${quarter}.2` },
    { id: crypto.randomUUID(), name: `${shortYear}.${quarter}.3` },
    { id: crypto.randomUUID(), name: `${shortYear}.${quarter}.4` },
    { id: crypto.randomUUID(), name: "IP" },
  ];
}

export function useOnboardSuggestions() {
  /**
   * Generate session from preset
   * @param preset - Preset to base session on
   * @returns Session generation result
   */
  function generateFromPreset(preset: Preset): SessionGenerationResult {
    const { quarter, fullYear } = getQuarterAndYear();

    return {
      sessionName: `Q${quarter} ${fullYear} - ${preset.name}`,
      teams: preset.teams.map(createTeamFromName),
      iterations: mapDefaultIterations(getDefaultIterations()),
      presetId: preset.id,
    };
  }

  /**
   * Generate fallback session for error cases
   * @param preset - Optional preset for fallback context
   * @returns Fallback session generation result
   */
  function generateFallback(preset?: Preset): SessionGenerationResult {
    if (preset) {
      return generateFromPreset(preset);
    }

    return {
      sessionName: getDefaultSessionName(),
      teams: mapDefaultTeams(getDefaultTeams()),
      iterations: mapDefaultIterations(getDefaultIterations()),
      presetId: "",
    };
  }

  return {
    generateFromPreset,
    generateFallback,
  };
}

// Helper: Create team from preset name
function createTeamFromName(name: string): OnboardTeam {
  return { id: crypto.randomUUID(), name };
}

// Helper: Map default teams from utils
function mapDefaultTeams(teams: DefaultTeamData[]): OnboardTeam[] {
  return teams.map((t) => ({ id: t.id, name: t.name }));
}

// Helper: Map default iterations from utils
function mapDefaultIterations(iterations: DefaultIterationData[]): OnboardIteration[] {
  return iterations.map((i) => ({ id: i.id, name: i.name }));
}
